import { useEffect, useState } from "react";
import { FileCheck, FileText, Loader2, X, Maximize2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { DOC_STATUS, fmtDate } from "@/lib/util";
import { BackBar, Loader, Empty, Pill, docColor, useOpenFile } from "@/components/common";
import { tr as t, Auto } from "@/lib/i18n";

const isImg = (d) => (d.content_type || "").startsWith("image/");

function Preview({ d, onOpen }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let on = true;
    if (isImg(d) && d.file_id) { fileUrl(d.file_id).then((u) => { if (on) { setSrc(u); } }).catch(() => {}); }
    return () => { on = false; };
  }, [d]);
  return (
    <button type="button" data-testid={`my-doc-preview-${d.id}`} onClick={() => onOpen(d, src)}
      className="relative w-full h-44 bg-slate-100 flex items-center justify-center overflow-hidden">
      {isImg(d) ? (src ? <img src={src} alt={d.name} className="w-full h-full object-cover" /> : <Loader2 className="w-8 h-8 animate-spin text-slate-400" />)
        : <span className="flex flex-col items-center text-green-700"><FileText className="w-14 h-14" /><b className="text-sm mt-1">PDF</b></span>}
      <span className="absolute bottom-2 end-2 w-9 h-9 rounded-full bg-black/55 text-white flex items-center justify-center"><Maximize2 className="w-5 h-5" /></span>
    </button>
  );
}

function FullView({ view, onClose }) {
  if (!view) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-black/95 flex flex-col" data-testid="doc-fullview" onClick={onClose}>
      <div className="flex items-center justify-between p-3 text-white safe-top">
        <b className="truncate"><Auto text={view.d.name} /></b>
        <button data-testid="doc-fullview-close" onClick={onClose} className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center"><X className="w-6 h-6" /></button>
      </div>
      <div className="flex-1 flex items-center justify-center p-2 overflow-auto">
        {view.src ? <img src={view.src} alt={view.d.name} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} /> : <Loader2 className="w-10 h-10 animate-spin text-white" />}
      </div>
    </div>
  );
}

export default function MyDocuments() {
  const [items, setItems] = useState(null);
  const [view, setView] = useState(null);
  const openFile = useOpenFile();
  useEffect(() => { api.get("/my/documents").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  const open = async (d, src) => (isImg(d) ? setView({ d, src: src || (await fileUrl(d.file_id).catch(() => null)) }) : openFile(d.file_id));
  return (
    <div>
      <BackBar title={t("مستنداتي")} to="/driver" />
      <div className="p-4 space-y-4">
        {items === null ? <Loader /> : items.length === 0 ? <Empty icon={FileCheck} text={t("لا توجد مستندات بعد")} /> : items.map((d) => (
          <div key={d.id} data-testid={`my-doc-${d.doc_type}`} className="card overflow-hidden p-0">
            {d.file_id && <Preview d={d} onOpen={open} />}
            <div className="p-4">
              <b className="block text-lg"><Auto text={d.name} /></b>
              <span className="block text-sm text-slate-500">{d.number && <span className="ltr">{d.number}</span>} {d.expiry_date && `• ${t("ينتهي {d}", { d: fmtDate(d.expiry_date) })}`}</span>
              <span className="mt-1 inline-block"><Pill color={docColor(d.status)}>{t(DOC_STATUS[d.status] || "")}{d.status === "expiring" ? ` ${t("({n} يوم)", { n: d.days_left })}` : ""}</Pill></span>
            </div>
          </div>
        ))}
      </div>
      <FullView view={view} onClose={() => setView(null)} />
    </div>
  );
}
